import type { QueryRowType } from "@rocicorp/zero";
import { useCallback, useEffect, useMemo, useState } from "react";
import { queries, roundedHoursAgo } from "../../shared/queries";
import { config } from "../environment";
import { useGetPresence } from "../hooks/useGetPresence";
import { useHasAccess } from "../hooks/useHasAccess";
import { useIsMobile } from "../hooks/useIsMobile";
import { useQuery } from "../hooks/useQuery";
import { useSpaceDisplayName } from "../hooks/useSpaceDisplayName";
import { useUrlSort } from "../hooks/useUrlSort";
import { isSpaceMember, useSpaces } from "../hooks/useSpaces";
import { branchLastActivity, branchRole, hasLiveVersion } from "../lib/branch";
import { connectorAvatarItems, mainBranchConnectorIds } from "../lib/connector";
import { makeUrl, routes, useCurrentUrl, useRouteMatch } from "../lib/urls";
import { userDisplayName } from "../lib/userDisplayName";
import { workflowReadmeSnippet } from "../lib/workflowReadmeSnippet";
import {
  type TriggerType,
  triggerTypeOrder,
  workflowTriggerTypes,
} from "../lib/workflowTrigger";
import { ActivityFacepile } from "./ActivityFacepile";
import { Backdrop } from "./Backdrop";
import { BranchList } from "./BranchList";
import {
  BuildStatus,
  getBranchListStatus,
  type StatusFilterValue,
  statusFilterOrder,
} from "./BuildStatus";
import { ConnectorAvatars } from "./ConnectorAvatars";
import { CreateWorkflow, HERO_MIN_GAP } from "./CreateWorkflow";
import { DismissibleHeroLayout } from "./DismissibleHeroLayout";
import { EmptyState } from "./EmptyState";
import { TriggerTypes } from "./TriggerTypes";
import {
  hasActiveFilters,
  ListFilter,
  ListFilterPills,
  parseMultiFilter,
} from "./ListFilter";
import { ListTable } from "./ListTable";
import { RecentsTabHeader } from "./RecentsTabHeader";
import { SpaceBadge } from "./SpaceBadge";
import { SpaceDropdown } from "./SpaceDropdown";
import { Text } from "./Text";
import { WorkflowActionsMenu } from "./WorkflowActionsMenu";
import {
  workflowListFilters,
  workflowListSortColumns,
} from "./workflowListFilters";
import { WorkflowNameCell } from "./WorkflowNameCell";

type RecentPresence = QueryRowType<
  ReturnType<typeof queries.userRecentWorkflows>
>;
type RecentWorkflow = NonNullable<RecentPresence["workflow"]>;
type RecentSpace = NonNullable<RecentWorkflow["space"]>;
type RecentCreator = NonNullable<RecentWorkflow["createdBy"]>;
type ConnectorInfo = QueryRowType<ReturnType<typeof queries.connectorsIndex>>;

function getWorkflowConnectors(
  workflow: Pick<RecentWorkflow, "branches">
): string[] {
  return mainBranchConnectorIds(workflow.branches);
}

export function Recents() {
  const isMobile = useIsMobile();
  const activitySince = roundedHoursAgo(24);
  const [allConnectors] = useQuery(queries.connectorsIndex());
  const [recentPresences, recentPresencesResult] = useQuery(
    queries.userRecentWorkflows({
      activitySince,
    })
  );
  const [draftBranches, draftBranchesResult] = useQuery(
    queries.userDraftBranches({ activitySince })
  );
  const [isAutofixTab] = useRouteMatch(routes.recents.autofix);
  const [isAutotuneTab] = useRouteMatch(routes.recents.autotune);
  const [navBranches] = useQuery(queries.navBranches());
  const autofixCount = navBranches.filter(
    (b) => branchRole(b) === "autofix"
  ).length;
  const autotuneCount = navBranches.filter(
    (b) => branchRole(b) === "autotune"
  ).length;
  const [allSpaces] = useSpaces({ settingsView: false });
  const spaces = useMemo(() => allSpaces.filter(isSpaceMember), [allSpaces]);
  const [preferences] = useQuery(queries.userPreferences());
  const displayName = useSpaceDisplayName();

  const connectorById = useMemo(
    () => new Map(allConnectors.map((a) => [a.id, a])),
    [allConnectors]
  );
  const backingAppByWorkflowId = useMemo(
    () =>
      new Map(
        allConnectors
          .filter((a) => a.backingWorkflowId && !a.fresh)
          .map((a) => [a.backingWorkflowId!, a])
      ),
    [allConnectors]
  );

  const { presentByWorkflow: presentUserIdsByWorkflow } = useGetPresence({
    enabled: recentPresencesResult.type === "complete",
  });

  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null);
  // useHasAccess falls back to tenant scope without a scopeId, so don't gate on
  // it before the default space resolves — submit is already blocked without one.
  const canWriteSelectedSpace = useHasAccess(
    "space:write",
    selectedSpaceId ?? undefined
  );
  const canWrite = !selectedSpaceId || canWriteSelectedSpace;
  const { sortColumn, sortDirection, handleSort, sortableColumns } = useUrlSort(
    routes.recents,
    workflowListSortColumns
  );
  const {
    status: statusParam,
    space: spaceParam,
    app: appParam,
    creator: creatorParam,
    trigger: triggerParam,
  } = useCurrentUrl(routes.recents);
  const statusFilter: StatusFilterValue | null = (
    statusFilterOrder as readonly string[]
  ).includes(statusParam)
    ? (statusParam as StatusFilterValue)
    : null;
  const triggerFilters = parseMultiFilter(triggerParam);
  const spaceFilters = parseMultiFilter(spaceParam);
  const appFilters = parseMultiFilter(appParam);
  const creatorFilters = parseMultiFilter(creatorParam);
  const [heroHeight, setHeroHeight] = useState(isMobile ? 160 : 240);
  const handleFormHeightChange = useCallback(
    (height: number) => setHeroHeight(height + HERO_MIN_GAP * 2),
    []
  );

  const [personalSpace] = useQuery(queries.personalSpace());
  const personalSpaceId = personalSpace?.id ?? null;
  useEffect(() => {
    if (selectedSpaceId || spaces.length === 0) return;
    const defaultId = preferences?.defaultSpace;
    const preferred = spaces.find((s) => s.id === defaultId);
    const personal = spaces.find((s) => s.id === personalSpaceId);
    setSelectedSpaceId(preferred?.id ?? personal?.id ?? spaces[0]?.id ?? null);
  }, [spaces, selectedSpaceId, preferences?.defaultSpace, personalSpaceId]);

  const viewedWorkflowIds = new Set(
    recentPresences.map((p) => p.workflow?.id).filter(Boolean)
  );
  const unviewedDraftWorkflows = new Map<string, RecentWorkflow>();
  for (const branch of draftBranches) {
    const workflow = branch.workflow;
    if (!workflow?.space || viewedWorkflowIds.has(workflow.id)) continue;
    unviewedDraftWorkflows.set(workflow.id, workflow);
  }
  const recentWorkflows = [
    ...recentPresences
      .filter((p) => p.workflow)
      .map((p) => ({
        workflow: p.workflow!,
        space: p.workflow!.space!,
        lastSeenAt: p.lastSeenAt as number | undefined,
      })),
    ...Array.from(unviewedDraftWorkflows.values(), (workflow) => ({
      workflow,
      space: workflow.space!,
      lastSeenAt: undefined as number | undefined,
    })),
  ];

  const workflowRecency = (item: (typeof recentWorkflows)[number]): number => {
    const branchActivity = item.workflow.branches
      .filter((b) => b.name !== "main")
      .reduce((max, b) => Math.max(max, branchLastActivity(b)), 0);
    return Math.max(
      item.workflow.updatedAt,
      item.lastSeenAt ?? 0,
      branchActivity
    );
  };

  const getRecentBuildStatus = (item: (typeof recentWorkflows)[number]) => {
    const branch = item.workflow.branches[0];
    if (!branch || branch.name === "main") return null;
    return getBranchListStatus(branch, item.lastSeenAt);
  };

  const availableStatuses = new Set<StatusFilterValue>();
  const availableSpaces = new Map<string, RecentSpace>();
  const availableAppIds = new Set<string>();
  const availableCreators = new Map<string, RecentCreator>();
  const availableTriggerTypes = new Set<TriggerType>();
  for (const item of recentWorkflows) {
    const status = getRecentBuildStatus(item);
    if (status) availableStatuses.add(status);
    for (const type of workflowTriggerTypes(item.workflow.branches)) {
      availableTriggerTypes.add(type);
    }
    if (item.space) availableSpaces.set(item.space.id, item.space);
    for (const appId of getWorkflowConnectors(item.workflow)) {
      availableAppIds.add(appId);
    }
    if (item.workflow.createdByUserId && item.workflow.createdBy) {
      availableCreators.set(
        item.workflow.createdByUserId,
        item.workflow.createdBy
      );
    }
  }
  const availableStatusOptions = statusFilterOrder.filter((s) =>
    availableStatuses.has(s)
  );
  const availableTriggerOptions = triggerTypeOrder.filter((t) =>
    availableTriggerTypes.has(t)
  );
  const availableSpaceOptions = [...availableSpaces.values()].sort((a, b) =>
    displayName(a).localeCompare(displayName(b))
  );
  const availableApps = [...availableAppIds]
    .map((id) => connectorById.get(id))
    .filter((a): a is ConnectorInfo => a != null)
    .sort((a, b) => a.name.localeCompare(b.name));
  const availableCreatorOptions = [...availableCreators.values()].sort((a, b) =>
    userDisplayName(a).localeCompare(userDisplayName(b))
  );

  const filteredWorkflows = recentWorkflows.filter((item) => {
    if (statusFilter !== null) {
      if (getRecentBuildStatus(item) !== statusFilter) return false;
    }
    if (
      triggerFilters.length > 0 &&
      !workflowTriggerTypes(item.workflow.branches).some((t) =>
        triggerFilters.includes(t)
      )
    )
      return false;
    if (spaceFilters.length > 0) {
      if (!item.space || !spaceFilters.includes(item.space.id)) return false;
    }
    if (appFilters.length > 0) {
      const ids = getWorkflowConnectors(item.workflow);
      if (!appFilters.some((id) => ids.includes(id))) return false;
    }
    if (creatorFilters.length > 0) {
      if (
        !item.workflow.createdByUserId ||
        !creatorFilters.includes(item.workflow.createdByUserId)
      )
        return false;
    }
    return true;
  });

  const filters = workflowListFilters({
    status: { options: availableStatusOptions, value: statusFilter },
    trigger: { options: availableTriggerOptions, values: triggerFilters },
    space: {
      options: availableSpaceOptions,
      values: spaceFilters,
      displayName,
    },
    app: { options: availableApps, values: appFilters },
    creator: { options: availableCreatorOptions, values: creatorFilters },
  });

  const sortedWorkflows = [...filteredWorkflows].sort((a, b) => {
    if (!sortColumn) {
      return workflowRecency(b) - workflowRecency(a);
    }

    let aVal: string | number;
    let bVal: string | number;

    switch (sortColumn) {
      case "name":
        aVal = a.workflow.name.toLowerCase();
        bVal = b.workflow.name.toLowerCase();
        break;
      case "space":
        aVal = displayName(a.space).toLowerCase();
        bVal = displayName(b.space).toLowerCase();
        break;
      case "createdAt":
        aVal = a.workflow.createdAt;
        bVal = b.workflow.createdAt;
        break;
      case "buildStatus": {
        const getStatusVal = (w: typeof a) => {
          const branch = w.workflow.branches[0];
          if (!branch || branch.name === "main") return 0;
          const status = getBranchListStatus(branch, w.lastSeenAt);
          const priority: Record<string, number> = {
            waiting: 3,
            ready: 2,
            building: 1,
            thinking: 1,
          };
          return (
            (priority[status ?? ""] ?? 0) * 1e15 + (branch.lastChatEntryAt ?? 0)
          );
        };
        aVal = getStatusVal(a);
        bVal = getStatusVal(b);
        break;
      }
      case "createdBy":
        aVal = (
          a.workflow.createdBy ? userDisplayName(a.workflow.createdBy) : ""
        ).toLowerCase();
        bVal = (
          b.workflow.createdBy ? userDisplayName(b.workflow.createdBy) : ""
        ).toLowerCase();
        break;
      default:
        return 0;
    }

    if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const displayedWorkflows = sortedWorkflows;

  const hasBuildStatus = displayedWorkflows.some(({ workflow, lastSeenAt }) => {
    const branch = workflow.branches[0];
    if (!branch || branch.name === "main") return false;
    return getBranchListStatus(branch, lastSeenAt) != null;
  });

  return (
    <>
      <title>{`Recents • ${config.title}`}</title>
      <Backdrop permanentNavigation>
        <DismissibleHeroLayout
          heroHeight={heroHeight}
          hero={
            <CreateWorkflow
              spaceId={selectedSpaceId}
              canWrite={canWrite}
              headingAction={
                <SpaceDropdown
                  selectedSpaceId={selectedSpaceId}
                  onSelect={setSelectedSpaceId}
                />
              }
              onFormHeightChange={handleFormHeightChange}
              deferQueries={recentPresencesResult.type !== "complete"}
            />
          }
          tabHeader={
            <RecentsTabHeader
              workflowsCount={displayedWorkflows.length}
              autofixCount={autofixCount}
              autotuneCount={autotuneCount}
            />
          }
        >
          {isAutofixTab ? (
            <BranchList tab="autofix" />
          ) : isAutotuneTab ? (
            <BranchList tab="autotune" />
          ) : (
            <ListTable
              columns={{ activity: "fixed-activity" }}
              headers={
                isMobile
                  ? { name: "Workflow" }
                  : {
                      name: "Workflow",
                      buildStatus: hasBuildStatus ? "3B status" : "",
                      space: "Space",
                      apps: "Connectors",
                      trigger: "Trigger",
                      createdAt: "Created",
                      createdBy: "Created by",
                      activity: "",
                      actions: "",
                    }
              }
              sortableColumns={isMobile ? [] : sortableColumns}
              dateColumns={isMobile ? [] : ["createdAt", "buildStatus"]}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              footer={
                hasActiveFilters(filters) ? (
                  <ListFilterPills filters={filters} />
                ) : null
              }
              headerActions={<ListFilter filters={filters} />}
              emptyState={
                recentPresencesResult.type === "complete" &&
                draftBranchesResult.type === "complete" ? (
                  recentWorkflows.length > 0 ? (
                    <EmptyState
                      title="No matches"
                      description="Try a different filter."
                      iconName="history"
                    />
                  ) : (
                    <EmptyState
                      title="No recent workflows"
                      description="Workflows you’ve viewed appear here."
                      iconName="history"
                    />
                  )
                ) : null
              }
              items={displayedWorkflows.map(
                ({ workflow, space, lastSeenAt }) => {
                  return {
                    key: workflow.id,
                    link: makeUrl(routes.space.workflow, {
                      spaceId: space?.id ?? "",
                      workflowId: workflow.id,
                    }),
                    name: (hover) => (
                      <WorkflowNameCell
                        workflow={workflow}
                        hover={hover}
                        connectedAppName={
                          backingAppByWorkflowId.get(workflow.id)?.name
                        }
                        readmeDescription={workflowReadmeSnippet(
                          workflow.branches
                        )}
                      />
                    ),
                    buildStatus: (() => {
                      const branch = workflow.branches[0];
                      if (!branch || branch.name === "main") return null;
                      return (
                        <BuildStatus
                          status={getBranchListStatus(branch, lastSeenAt)}
                        />
                      );
                    })(),
                    space: space ? <SpaceBadge space={space} /> : null,
                    trigger: <TriggerTypes branches={workflow.branches} />,
                    apps: (
                      <ConnectorAvatars
                        connected={connectorAvatarItems(
                          getWorkflowConnectors(workflow),
                          connectorById
                        )}
                      />
                    ),
                    createdAt: (
                      <Text variant="text-small">
                        {new Date(workflow.createdAt).toLocaleDateString()}
                      </Text>
                    ),
                    createdBy: (
                      <Text
                        variant="text-small"
                        color="var(--text-primary)"
                        ellipsis
                      >
                        {workflow.createdBy
                          ? userDisplayName(workflow.createdBy)
                          : null}
                      </Text>
                    ),
                    activity: (
                      <ActivityFacepile
                        contributors={workflow.contributors}
                        presentUserIds={presentUserIdsByWorkflow.get(
                          workflow.id
                        )}
                      />
                    ),
                    actions: (hover) => {
                      const isFavorite = (workflow.favorites?.length ?? 0) > 0;
                      const workflowSpaceId = space?.id ?? "";
                      return (
                        <div style={{ opacity: hover ? 1 : 0 }}>
                          <WorkflowActionsMenu
                            workflowId={workflow.id}
                            spaceId={workflowSpaceId}
                            hasLiveVersion={hasLiveVersion(workflow.branches)}
                            isFavorite={isFavorite}
                            autofixEnabled={workflow.autofixEnabled ?? true}
                            autoImproveEnabled={
                              workflow.autoImproveEnabled ?? false
                            }
                          />
                        </div>
                      );
                    },
                  };
                }
              )}
            />
          )}
        </DismissibleHeroLayout>
      </Backdrop>
    </>
  );
}
